/**
 * @spec docs/superpowers/specs/2026-10-15-staking-ui-wallet-actions-design.md §7.3
 * @standard docs/standards/staking-ui-wallet-actions.md#errors
 *
 * Wallet error classification: the only classifier in the app. Provider errors from MetaMask,
 * Coinbase Wallet, WalletConnect and Privy, and the WalletActionErrors raised by
 * src/lib/wallet-actions, map to one category and its user-facing copy. UI code reads the copy
 * through describeWalletError (below), never by matching error text itself.
 */

import { getChainDisplayName, getSwitchableChain } from './chain-registry';
import { sanitizeErrorMessage } from './error-sanitizer';
import { WrongNetworkError, type WrongNetworkInfo } from './network-gate';
import type { TxRef } from './tx-tracker/types';
import { WalletActionError, type WalletActionErrorCategory } from './wallet-actions/errors';

export type SigningErrorCategory =
  | 'hardware_blind_signing'
  | 'wrong_network'
  | 'user_rejected'
  | 'wallet_declined'
  | 'wallet_unavailable'
  | 'wrong_account'
  | 'chain_error'
  | 'unknown';

/** Every category a wallet failure can surface with (spec §7.3). */
export type WalletErrorCategory = WalletActionErrorCategory;

export interface ClassifiedSigningError {
  category: WalletErrorCategory;
  message: string;
  /** Set for 'wrong_network': the chain the wallet is on. */
  currentChainId?: number;
  /** Set for 'wrong_network', 'unsupported-chain' and 'insufficient-gas' when known. */
  requiredChainId?: number;
}

/** What the caller knows that the error may not carry. */
export interface ClassifyContext {
  requiredChainId?: number | null;
}

/** Spec §7.1, verbatim. */
export const UNSUPPORTED_ACCOUNT_MESSAGE =
  "This wallet's smart account can't receive staking positions or sign this USDC payment. Use a standard account or your Phenom wallet.";

const LEDGER_BLIND_SIGNING_MESSAGE =
  'Your Ledger blocked this signature. On the device, open the Ethereum app → Settings and enable Blind signing (or Raw messages), then try again. This setting resets after Ethereum app updates.';
const USER_REJECTED_MESSAGE = 'You rejected the signature request';
const WALLET_DECLINED_MESSAGE =
  'Your wallet declined the request. If you did not reject it, your wallet may have no account on the required network: add one in your wallet app such as Ledger Live, then disconnect and reconnect.';
const WALLET_UNAVAILABLE_MESSAGE = 'Your wallet is not available. Please unlock it and try again';
const WRONG_ACCOUNT_MESSAGE = 'The requested account is not active in your wallet';
const CHAIN_ERROR_MESSAGE = 'Please switch to the correct network in your wallet';
const UNKNOWN_FALLBACK = 'Failed to sign message';

/** EIP-1193 4902 "unrecognized chain" and Privy's 4901 "Unsupported chainId", at any depth. */
const UNSUPPORTED_CHAIN_CODES: readonly number[] = [4901, 4902];
/** Wallet wording for a chain it does not know or a WalletConnect session that lacks it. */
const UNSUPPORTED_CHAIN_TEXT =
  /unsupported chain|unrecognized chain|chain\b.{0,40}\bnot (?:supported|approved|configured)|missing or invalid\. request\(\) chainid/i;
const INSUFFICIENT_GAS_TEXT = /insufficient funds|INSUFFICIENT_FUNDS/i;

function chainLabel(chainId: number | null | undefined): string {
  return chainId ? getChainDisplayName(chainId) : 'the required network';
}

/** An unsupported chain is fixed by reconnecting, never by switching (spec §7.3). */
export function unsupportedChainMessage(requiredChainId: number | null | undefined): string {
  const chain = chainLabel(requiredChainId);
  return `Your wallet session doesn't include ${chain}. Disconnect your wallet, then reconnect and approve ${chain}.`;
}

export function insufficientGasMessage(requiredChainId: number | null | undefined): string {
  const symbol = requiredChainId
    ? getSwitchableChain(requiredChainId)?.nativeCurrency.symbol
    : undefined;
  return `Not enough ${symbol ?? 'native token'} to pay the network fee. Add some to this wallet first.`;
}

/** MetaMask's typed-data chain check. First id: payload domain (required). Second: active chain. */
const METAMASK_CHAIN_MISMATCH =
  /Provided chainId "?(\d+)"? must match the active chainId "?(\d+)"?/i;

function findWrongNetworkError(error: unknown, depth = 0): WrongNetworkError | null {
  if (error instanceof WrongNetworkError) return error;
  if (!error || typeof error !== 'object' || depth > 3) return null;
  const e = error as Record<string, unknown>;
  for (const key of ['cause', 'data', 'error', 'originalError']) {
    if (key in e) {
      const found = findWrongNetworkError(e[key], depth + 1);
      if (found) return found;
    }
  }
  return null;
}

function findWrongNetwork(error: unknown, text: string): WrongNetworkInfo | null {
  const typed = findWrongNetworkError(error);
  if (typed)
    return { currentChainId: typed.currentChainId, requiredChainId: typed.requiredChainId };
  const match = METAMASK_CHAIN_MISMATCH.exec(text);
  if (!match) return null;
  return { requiredChainId: Number(match[1]), currentChainId: Number(match[2]) };
}

/** The wrong-network ids behind a signing failure, or null when it was not a network mismatch. */
export function getWrongNetworkInfo(error: unknown): WrongNetworkInfo | null {
  const result = classifySigningError(error);
  return result.category === 'wrong_network' &&
    result.currentChainId !== undefined &&
    result.requiredChainId !== undefined
    ? { currentChainId: result.currentChainId, requiredChainId: result.requiredChainId }
    : null;
}

function withRequired(chainId: number | null | undefined): { requiredChainId?: number } {
  return chainId === null || chainId === undefined ? {} : { requiredChainId: chainId };
}

/** Copy for a category when nothing more specific is known. */
function copyFor(category: SigningErrorCategory, requiredChainId: number | null): string {
  switch (category) {
    case 'hardware_blind_signing':
      return LEDGER_BLIND_SIGNING_MESSAGE;
    case 'wrong_network':
      return `Switch your wallet to ${chainLabel(requiredChainId)} and try again.`;
    case 'user_rejected':
      return USER_REJECTED_MESSAGE;
    case 'wallet_declined':
      return WALLET_DECLINED_MESSAGE;
    case 'wallet_unavailable':
      return WALLET_UNAVAILABLE_MESSAGE;
    case 'wrong_account':
      return WRONG_ACCOUNT_MESSAGE;
    case 'chain_error':
      return CHAIN_ERROR_MESSAGE;
    case 'unknown':
      return UNKNOWN_FALLBACK;
  }
}

function classifyActionError(error: WalletActionError): ClassifiedSigningError {
  const required = error.requiredChainId;
  switch (error.category) {
    case 'unsupported-account':
      return { category: 'unsupported-account', message: UNSUPPORTED_ACCOUNT_MESSAGE };
    case 'unsupported-chain':
      return {
        category: 'unsupported-chain',
        // The embedded wallet has no session to reconnect; prepareWallet's copy says what failed.
        message:
          error.walletKind === 'embedded' && error.message.length > 0
            ? error.message
            : unsupportedChainMessage(required),
        ...withRequired(required),
      };
    case 'insufficient-gas':
      return {
        category: 'insufficient-gas',
        message: insufficientGasMessage(required),
        ...withRequired(required),
      };
    case 'unknown': {
      // A wrap that could not name the failure: the cause may still be specific.
      const inner = classifySigningError(error.cause, { requiredChainId: required });
      if (inner.category !== 'unknown') return inner;
      return { category: 'unknown', message: error.message || inner.message };
    }
    default: {
      // wrap.ts derives the category from the wallet's own error, so the cause classifies the
      // same and keeps its detail (the wrong-network chain ids). An error our code authored
      // (prepare.ts, sign.ts) has no such cause and keeps its own message.
      const inner = classifySigningError(error.cause, { requiredChainId: required });
      if (inner.category === error.category) return inner;
      return {
        category: error.category,
        message: error.message || copyFor(error.category, required),
        ...withRequired(required),
      };
    }
  }
}

/**
 * Classify a wallet failure into a category and user-facing copy.
 */
export function classifySigningError(
  error: unknown,
  context: ClassifyContext = {}
): ClassifiedSigningError {
  if (error instanceof WalletActionError) return classifyActionError(error);

  // Providers wrap: the EIP-1193 code and the wallet's own wording routinely sit in `cause`,
  // `data.originalError` or `error` rather than on the object we are handed, so a Ledger Live
  // decline arrived as a bare "Internal JSON-RPC error" and fell through to `unknown`. Match on
  // the whole nested walk; the ROOT message still supplies the `unknown` fallback text so the
  // message the user sees stays the one the provider put in front.
  const codes = collectErrorCodes(error);
  const text = collectErrorText(error);
  const message = extractErrorMessage(error);
  const required = context.requiredChainId ?? null;

  // Ledger refused an EIP-712 payload it cannot clear-sign. Checked before the rejection matcher
  // because the same error can carry "declined" wording from a WalletConnect wrapper.
  if (isLedgerBlindSigningRefusal(error)) {
    return { category: 'hardware_blind_signing', message: LEDGER_BLIND_SIGNING_MESSAGE };
  }

  // MetaMask refuses typed data whose domain chainId differs from the active chain; prepareWallet
  // refuses the same condition before the prompt. Before the rejection matcher so a wrapper's
  // wording cannot reclassify it; after blind signing, the more specific hardware failure.
  const wrongNetwork = findWrongNetwork(error, text);
  if (wrongNetwork) {
    return {
      category: 'wrong_network',
      message: `Your wallet is on ${getChainDisplayName(wrongNetwork.currentChainId)}. Switch to ${getChainDisplayName(wrongNetwork.requiredChainId)} and try again.`,
      ...wrongNetwork,
    };
  }

  // EIP-1193 code 4001: User rejected request
  if (codes.includes(4001) || /reject|denied|cancel/i.test(text)) {
    return { category: 'user_rejected', message: USER_REJECTED_MESSAGE };
  }

  // The wallet does not know the chain (4902, also nested; Privy's 4901), or a WalletConnect
  // session was paired without it. Reconnecting is the fix, not switching.
  if (
    codes.some((code) => UNSUPPORTED_CHAIN_CODES.includes(code)) ||
    UNSUPPORTED_CHAIN_TEXT.test(text)
  ) {
    return {
      category: 'unsupported-chain',
      message: unsupportedChainMessage(required),
      ...withRequired(required),
    };
  }

  // "declined" WITHOUT 4001 is ambiguous: Ledger Live's WalletConnect handler answers
  // "Message signed declined" when it has no account for the requested chain, indistinguishable
  // from a real decline by text alone. Say so instead of blaming the user.
  if (/declin/i.test(text)) {
    return { category: 'wallet_declined', message: WALLET_DECLINED_MESSAGE };
  }

  // Wallet locked or request already pending
  if (codes.includes(-32002) || /locked|already pending|not unlocked/i.test(text)) {
    return { category: 'wallet_unavailable', message: WALLET_UNAVAILABLE_MESSAGE };
  }

  // Wrong account / not authorized
  if (codes.includes(-32602) || /not been authorized|account.*not/i.test(text)) {
    return { category: 'wrong_account', message: WRONG_ACCOUNT_MESSAGE };
  }

  // Not enough native currency for the fee (broadcast-time wallet answer)
  if (INSUFFICIENT_GAS_TEXT.test(text)) {
    return {
      category: 'insufficient-gas',
      message: insufficientGasMessage(required),
      ...withRequired(required),
    };
  }

  // Chain / network mismatch
  if (/chain|mismatch|wrong.*network|switch.*network/i.test(text)) {
    return { category: 'chain_error', message: CHAIN_ERROR_MESSAGE };
  }

  // Unknown: include the actual message if it's short and clean
  const fallback = message.length > 0 && message.length <= 120 ? message : UNKNOWN_FALLBACK;
  return { category: 'unknown', message: fallback };
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  if (typeof error === 'string') return error;
  return '';
}

/** Ledger Ethereum app status word for "data not allowed" — what an unfiltered EIP-712 sign returns
 *  when Blind signing is off. hw-app-eth only maps it to a friendly error on the TRANSACTION path,
 *  so typed-data failures reach us raw, often wrapped by WalletConnect or MetaMask. */
const LEDGER_BLIND_SIGNING_STATUS = 0x6a80;

/** Walk the error plus the usual wrapping fields, collecting every message/name we can find. */
function collectErrorText(error: unknown, depth = 0): string {
  if (!error || typeof error !== 'object' || depth > 3) {
    return typeof error === 'string' ? error : '';
  }
  const e = error as Record<string, unknown>;
  const parts: string[] = [];
  if (typeof e.name === 'string') parts.push(e.name);
  if (typeof e.message === 'string') parts.push(e.message);
  for (const key of ['cause', 'data', 'error', 'originalError']) {
    if (key in e) parts.push(collectErrorText(e[key], depth + 1));
  }
  return parts.filter(Boolean).join(' ');
}

/** Collect the EIP-1193 codes from the error and the usual wrapping fields, root first. */
function collectErrorCodes(error: unknown, depth = 0): number[] {
  if (!error || typeof error !== 'object' || depth > 3) return [];
  const e = error as Record<string, unknown>;
  const codes: number[] = [];
  if (typeof e.code === 'number') codes.push(e.code);
  for (const key of ['cause', 'data', 'error', 'originalError']) {
    if (key in e) codes.push(...collectErrorCodes(e[key], depth + 1));
  }
  return codes;
}

function hasLedgerBlindSigningStatus(error: unknown, depth = 0): boolean {
  if (!error || typeof error !== 'object' || depth > 3) return false;
  const e = error as Record<string, unknown>;
  if (e.statusCode === LEDGER_BLIND_SIGNING_STATUS) return true;
  return ['cause', 'data', 'error', 'originalError'].some(
    (key) => key in e && hasLedgerBlindSigningStatus(e[key], depth + 1)
  );
}

function isLedgerBlindSigningRefusal(error: unknown): boolean {
  if (hasLedgerBlindSigningStatus(error)) return true;
  const text = collectErrorText(error);
  return /0x6a80|\b6a80\b|blind[\s-]?sign|EthAppPleaseEnableContractData|enable .*contract data/i.test(
    text
  );
}

/** What a flow shows for a wallet failure. */
export interface WalletErrorView {
  category: WalletErrorCategory;
  message: string;
  /**
   * Set once the transaction was broadcast or handed to a relay (spec §7.3): the UI shows the
   * status and explorer link and never offers "try again".
   */
  txRef: TxRef | null;
  /** Set for a wrong-network failure, for the "Switch to {chain}" action. */
  wrongNetwork: WrongNetworkInfo | null;
}

export const SUBMITTED_FALLBACK_MESSAGE =
  'Your transaction was submitted. Follow its status on the block explorer.';

/**
 * Copy for a flow's failure that may or may not come from the wallet: a WalletActionError gets the
 * classified copy (describeWalletError); anything else (an API refusal, a config error) keeps its
 * sanitized message, so a backend reason is never rewritten into wallet advice.
 */
export function presentFailure(
  error: unknown,
  fallback: string,
  options?: { requiredChainId?: number | null }
): string {
  return error instanceof WalletActionError
    ? describeWalletError(error, fallback, options).message
    : sanitizeErrorMessage(error, fallback);
}

/**
 * The one presenter of wallet failures: classified copy for every category, the sanitized message
 * for unknown ones, and the txRef of anything already submitted.
 */
export function describeWalletError(
  error: unknown,
  fallback: string,
  context: ClassifyContext = {}
): WalletErrorView {
  if (error instanceof WalletActionError && error.txRef) {
    const authored = error.message.length > 0 && !/try again/i.test(error.message);
    return {
      category: classifySigningError(error, context).category,
      message: authored ? error.message : SUBMITTED_FALLBACK_MESSAGE,
      txRef: error.txRef,
      wrongNetwork: null,
    };
  }
  const classified = classifySigningError(error, context);
  const wrongNetwork =
    classified.category === 'wrong_network' &&
    classified.currentChainId !== undefined &&
    classified.requiredChainId !== undefined
      ? { currentChainId: classified.currentChainId, requiredChainId: classified.requiredChainId }
      : null;
  const message =
    classified.category === 'unknown'
      ? sanitizeErrorMessage(
          error instanceof WalletActionError ? new Error(classified.message) : error,
          fallback
        )
      : classified.message;
  return { category: classified.category, message, txRef: null, wrongNetwork };
}
